import { useEffect, useMemo, useRef } from 'preact/hooks';
import { useSignal, batch } from '@preact/signals';
import { FloatingButton } from './components/FloatingButton.js';
import { ChatWindow } from './components/ChatWindow.js';
import {
  createStore,
  appendMessage,
  mergeHistory,
  setStatus,
  setConversationId,
  startAiThinking,
  stopAiThinking,
  trackLocalSend,
  confirmAck,
  markSendRetried,
  dropPendingAttachmentSend,
  type Message,
  type Attachment,
} from './state/store.js';
import { savePersistedState, loadPersistedState } from './state/persistence.js';
import { createWsClient, type WsClient } from './ws/client.js';
import type { WsAttachment, WsIncoming } from './ws/types.js';
import { createTheme } from './styles/theme.js';
import { toAvailabilityState, type AvailabilityResponse } from './lib/availability.js';
import { isSendBlocked } from './lib/availability.js';
import { getPresenceId } from './presence.js';
import type { ComposedAttachment } from './components/InputComposer.js';
import { STRINGS } from './strings.js';
import { toAbsoluteUrl as toAbsoluteUrlImpl, mapAttachments as mapAttachmentsImpl } from './lib/attachments.js';

const SOFT_INVITE_DELAY_MS = 120_000;
const AVAILABILITY_POLL_MS = 60_000;
// Garde-fou : si aucune réponse IA ni agent n'arrive (mode auto), on
// n'affiche pas la bulle « … » indéfiniment.
const AI_THINKING_TIMEOUT_MS = 60_000;

type Ctx = {
  backendUrl: string;
  brandColor?: string;
  identityToken?: string;
  privacyUrl?: string;
};

const DEFAULT_PRIVACY_URL = 'https://www.bokame.com/politique-de-confidentialite/';

/** 4 photos maximum par message — même plafond que côté serveur. */
const MAX_ATTACHMENTS = 4;

/**
 * Reconstitue les pièces jointes à renvoyer lors d'une relance d'un message
 * resté "Non envoyé" : elles ont déjà été téléversées (elles portent un id
 * serveur et une URL signée), seul le rattachement au message reste à faire.
 * Si elles ont été purgées entre-temps (> 1 h), le serveur répond
 * `ATTACHMENT_UNAVAILABLE` et l'aperçu est retiré.
 */
function composedFromMessage(message: Message): ComposedAttachment[] {
  return (message.attachments ?? []).map((a) => ({ id: a.id, url: a.url }));
}

export function App({ ctx }: { ctx: Ctx }) {
  // Le store est créé une seule fois, avec des valeurs par défaut vides.
  // RGPD (contrat 2.1) : au montage on NE lit PAS le localStorage — la
  // restauration de l'historique n'a lieu qu'au premier clic (cf. start()).
  const store = useMemo(() => createStore(), []);
  const wsRef = useRef<WsClient | null>(null);
  const theme = createTheme(ctx.brandColor || '#2c5530');
  const triggerReconnect = useSignal(0);

  // Le widget ne déclenche identify/fetch/WS/localStorage qu'après le
  // premier clic du visiteur sur le bouton flottant. Ceci est distinct du
  // canal de présence « en direct » (presence.ts), démarré lui dès le
  // chargement de la page depuis index.tsx — voir le commentaire RGPD
  // détaillé là-bas.
  const started = useSignal(false);
  const softInviteTimer = useRef<number | null>(null);
  const aiThinkingTimer = useRef<number | null>(null);

  // Le serveur renvoie des URL racine-relatives (`/api/attachments/...`) :
  // le widget tourne sur bokame.com et l'API sur chat.bokame.com, donc on
  // préfixe ici. Volontairement côté client plutôt qu'une variable
  // d'environnement « URL publique » de plus côté serveur.
  function toAbsoluteUrl(url: string): string {
    return toAbsoluteUrlImpl(ctx.backendUrl, url);
  }

  function mapAttachments(list: WsAttachment[] | undefined): Attachment[] | undefined {
    return mapAttachmentsImpl(ctx.backendUrl, list);
  }

  /**
   * Téléverse une photo et renvoie son id serveur. La conversation est créée
   * si besoin (même création différée que pour un message texte : rien n'est
   * créé tant que le visiteur n'agit pas). Le message d'erreur renvoyé par
   * le serveur est déjà en français et affichable tel quel.
   */
  async function uploadAttachment(file: File): Promise<ComposedAttachment> {
    const convId = await ensureConversation();
    const form = new FormData();
    form.append('file', file);
    let res: Response;
    try {
      res = await fetch(`${ctx.backendUrl}/api/visitor/attachments?conversationId=${convId}`, {
        method: 'POST',
        credentials: 'include',
        // En-tête anti-CSRF : un formulaire HTML tiers auto-soumis ne peut
        // pas le poser, et sa présence force un preflight CORS restreint par
        // ALLOWED_ORIGINS côté serveur.
        headers: { 'x-bokame-client': 'bokame-widget' },
        body: form,
      });
    } catch {
      throw new Error(STRINGS.attachErrorGeneric);
    }
    if (!res.ok) {
      const body = (await res.json().catch(() => null)) as { message?: string } | null;
      throw new Error(body?.message ? `${body.message}.` : STRINGS.attachErrorGeneric);
    }
    const data = (await res.json()) as { id: string; url: string };
    return { id: data.id, url: toAbsoluteUrl(data.url) };
  }

  function ensureConversation(): Promise<string> {
    if (store.conversationId.value) return Promise.resolve(store.conversationId.value);
    return fetch(`${ctx.backendUrl}/api/conversations`, {
      method: 'POST',
      credentials: 'include',
    })
      .then((r) => r.json())
      .then((j: { id: string }) => {
        setConversationId(store, j.id);
        return j.id;
      });
  }

  async function identifyVisitor(): Promise<void> {
    try {
      // Send the current page along with identify so the agent map can show
      // which URL the visitor is currently viewing.
      const payload: Record<string, unknown> = {
        ...(ctx.identityToken ? { identityToken: ctx.identityToken } : {}),
        url: window.location.href,
        title: document.title?.slice(0, 300) ?? '',
        // Relie ce visiteur (identifié via cookie) à son entrée dans le
        // canal de présence « en direct », démarré indépendamment du chat
        // (cf. presence.ts / index.tsx) — l'agent voit alors qu'il est en
        // conversation directement depuis la vue « En direct ».
        presenceId: getPresenceId(),
      };
      const res = await fetch(`${ctx.backendUrl}/api/visitor/identify`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      });
      // Auto-reprise : si le backend renvoie une conversation ouverte pour
      // ce visiteur, on la reprend (pas de nouvelle conv créée à la volée).
      const data = (await res.json().catch(() => null)) as {
        activeConversationId?: string | null;
      } | null;
      if (data?.activeConversationId && !store.conversationId.value) {
        setConversationId(store, data.activeConversationId);
      }
    } catch {
      /* ignore — anonymous fallback */
    }
  }

  async function fetchAvailability(): Promise<void> {
    try {
      // Endpoint public "sans cookie, sans effet de bord" (contrat 1.2) :
      // pas de credentials envoyées.
      const res = await fetch(`${ctx.backendUrl}/api/visitor/availability`, {
        credentials: 'omit',
      });
      if (!res.ok) return;
      const data = (await res.json()) as AvailabilityResponse;
      batch(() => {
        store.availability.value = toAvailabilityState(data.online);
        store.availabilityMessage.value = data.message;
        store.aiMode.value = data.aiMode;
      });
    } catch {
      /* on garde le dernier état connu (ou 'unknown') */
    }
  }

  // Rattrapage d'historique : la WS peut être gelée/coupée sans que le
  // widget le sache immédiatement (iOS Safari en arrière-plan — cf. bug prod
  // du 29/09 : réponse agent envoyée pendant la coupure, jamais affichée au
  // retour). Appelée au (re)branchement WS et sur les événements de retour
  // au premier plan ; la fusion par id (mergeHistory) rend l'appel idempotent
  // et sans effet visible si rien n'a été manqué.
  async function resyncHistory(): Promise<void> {
    const convId = store.conversationId.value;
    if (!convId) return;
    try {
      const res = await fetch(`${ctx.backendUrl}/api/conversations/${convId}/messages/visitor`, {
        credentials: 'include',
      });
      if (!res.ok) return;
      const data = (await res.json()) as {
        messages: Array<{
          id: string;
          authorType: 'visitor' | 'ai' | 'agent' | 'system';
          content: string;
          createdAt: string;
          // D16 : sans ce champ, un message photo déjà reçu par WS perdait ses
          // pièces jointes dès la prochaine resync (mergeHistory écrase par id) —
          // cf. lib/attachments.ts pour le détail du bug.
          attachments?: WsAttachment[];
        }>;
      };
      const serverMsgs: Message[] = data.messages.map((m) => {
        const attachments = mapAttachments(m.attachments);
        return {
          id: m.id,
          role: m.authorType,
          content: m.content,
          createdAt: Date.parse(m.createdAt) || Date.now(),
          ...(attachments ? { attachments } : {}),
        };
      });
      store.messages.value = mergeHistory(store.messages.value, serverMsgs);
      savePersistedState(store);
    } catch {
      /* hors ligne ou erreur transitoire — retenté au prochain (re)branchement
         WS ou retour au premier plan */
    }
  }

  function clearSoftInviteTimer(): void {
    if (softInviteTimer.current !== null) {
      window.clearTimeout(softInviteTimer.current);
      softInviteTimer.current = null;
    }
  }

  function clearAiThinkingTimer(): void {
    if (aiThinkingTimer.current !== null) {
      window.clearTimeout(aiThinkingTimer.current);
      aiThinkingTimer.current = null;
    }
  }

  function scheduleAiThinkingTimeout(): void {
    clearAiThinkingTimer();
    aiThinkingTimer.current = window.setTimeout(() => {
      aiThinkingTimer.current = null;
      stopAiThinking(store);
    }, AI_THINKING_TIMEOUT_MS);
  }

  function scheduleSoftInvite(): void {
    clearSoftInviteTimer();
    softInviteTimer.current = window.setTimeout(() => {
      if (!store.emailCaptured.value) {
        store.showSoftInvite.value = true;
      }
    }, SOFT_INVITE_DELAY_MS);
  }

  function handleIncoming(msg: WsIncoming): void {
    if (msg.type === 'message') {
      const m = msg.message;
      batch(() => {
        const attachments = mapAttachments(m.attachments);
        appendMessage(store, {
          id: m.id,
          role: m.authorType,
          content: m.content,
          createdAt: Date.parse(m.createdAt) || Date.now(),
          ...(attachments ? { attachments } : {}),
        });
        if (m.authorType === 'agent') {
          store.escalation.value = 'agent_joined';
          // Un agent a répondu : l'invitation douce à laisser un e-mail
          // n'a plus lieu d'être (contrat 2.2).
          clearSoftInviteTimer();
          store.showSoftInvite.value = false;
          // Sécurité : un agent humain a pris la main, la bulle « … » (le
          // cas échéant) n'a plus lieu d'être, même en mode auto.
          clearAiThinkingTimer();
          stopAiThinking(store);
        }
        if (m.authorType === 'ai') {
          clearAiThinkingTimer();
          stopAiThinking(store);
        }
        if (!store.isOpen.value && m.authorType !== 'visitor') {
          store.unreadCount.value += 1;
        }
      });
      savePersistedState(store);
    }
    if (msg.type === 'error' && msg.code === 'ATTACHMENT_UNAVAILABLE') {
      // La photo n'a pas pu être rattachée (déjà envoyée depuis un autre
      // onglet, ou purgée au bout d'1 h) : aucun message n'existe côté
      // serveur, on retire donc la bulle optimiste plutôt que de laisser un
      // envoi fantôme.
      batch(() => {
        dropPendingAttachmentSend(store);
        store.sendError.value = STRINGS.attachExpired;
      });
      savePersistedState(store);
      return;
    }
    if (msg.type === 'ack') {
      // Réconciliation par ack (bug prod 30/09 : doublon du message visiteur
      // après une resync). Le serveur n'écho pas les messages visiteur sur la
      // WS ; il confirme via cet ack avec l'id définitif — on remplace le
      // `local-*` optimiste correspondant, en place, avant toute resync REST
      // (sinon mergeHistory verrait deux entrées pour le même envoi).
      confirmAck(store, msg.messageId);
      savePersistedState(store);
    }
  }

  useEffect(() => {
    if (!started.value) return;
    let cancelled = false;
    void (async () => {
      // identifyVisitor pose le cookie et reprend une conversation ouverte
      // si elle existe. La création d'une nouvelle conversation est différée
      // au premier envoi du visiteur (cf. deliver()), pour ne pas générer une
      // conversation vide à chaque ouverture du widget.
      await identifyVisitor();
      if (cancelled) return;
      const wsUrl = `${ctx.backendUrl.replace(/^http/, 'ws')}/ws/visitor`;
      wsRef.current = createWsClient({
        url: wsUrl,
        onMessage: handleIncoming,
        onStatusChange: (s) => {
          setStatus(store, s);
          if (s === 'connected') {
            // Ré-abonne explicitement ce socket à la conversation reprise
            // (sinon un socket reconnecté ne reçoit rien tant que le
            // visiteur n'a pas ré-écrit — cf. visitor-channel.ts) et rattrape
            // tout ce qui a pu être manqué pendant la coupure.
            const convId = store.conversationId.value;
            if (convId) wsRef.current?.send({ type: 'subscribe', conversationId: convId });
            void resyncHistory();
            retryFailedMessages();
          }
        },
      });
    })();
    return () => {
      cancelled = true;
      wsRef.current?.close();
    };
    // re-trigger on retry, and once started becomes true
  }, [started.value, triggerReconnect.value]);

  // Retour au premier plan : Safari iOS gèle/ferme la WS en arrière-plan
  // sans que l'app en soit toujours informée à temps (l'événement 'close'
  // peut n'arriver que bien après, ou jamais si le plafond de tentatives de
  // reconnexion avait déjà été atteint pendant que l'onglet était caché).
  // On force une reconnexion (no-op si le socket est déjà sain) et, dans
  // tous les cas, une resync REST — cause racine du bug prod du 29/09.
  useEffect(() => {
    if (!started.value) return;
    function onForeground(): void {
      wsRef.current?.reconnectNow();
      void resyncHistory();
    }
    function onVisibility(): void {
      if (document.visibilityState === 'visible') onForeground();
    }
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pageshow', onForeground);
    window.addEventListener('online', onForeground);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pageshow', onForeground);
      window.removeEventListener('online', onForeground);
    };
  }, [started.value]);

  useEffect(() => {
    if (!started.value || !store.isOpen.value) return;
    let cancelled = false;
    void fetchAvailability();
    const id = window.setInterval(() => {
      if (!cancelled) void fetchAvailability();
    }, AVAILABILITY_POLL_MS);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [started.value, store.isOpen.value]);

  // `retryLocalId` : présent uniquement pour une relance (manuelle via le
  // bouton "Réessayer", ou automatique à la reconnexion) d'un message déjà
  // affiché mais jamais parti — on réutilise alors son id `local-*` existant
  // au lieu d'en créer un nouveau, pour ne jamais dupliquer le message
  // (contrat quick win §9 : pas de doublon, mécanisme trackLocalSend/confirmAck
  // inchangé). `composed` porte alors les mêmes pièces jointes qu'au premier
  // essai : elles ont été téléversées avant l'échec, seul le lien au message
  // reste à faire.
  async function deliver(
    content: string,
    composed: ComposedAttachment[] = [],
    retryLocalId?: string,
  ): Promise<void> {
    const attachmentIds = composed.map((a) => a.id);
    // Création différée : si le visiteur n'a pas encore de conversation
    // (ni reprise ni créée), on en crée une au moment où il écrit son
    // premier message — pas avant.
    let convId = store.conversationId.value;
    if (!convId) {
      try {
        convId = await ensureConversation();
      } catch {
        return;
      }
    }
    const isFirstVisitorMessage = !store.messages.value.some(
      (m) => m.role === 'visitor' && !m.failed,
    );
    const localId = retryLocalId ?? `local-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    // Aperçu optimiste : on affiche déjà la photo via son URL signée (elle
    // existe côté serveur depuis le téléversement), donc pas de blob local à
    // révoquer ni de bulle vide en attendant l'ack.
    const optimisticAttachments: Attachment[] = composed.map((a) => ({
      id: a.id,
      mime: 'image/jpeg',
      size: 0,
      width: null,
      height: null,
      url: a.url,
      // `ComposedAttachment` (visiteur, images seulement) ne porte pas le
      // nom d'origine — l'ack/resync REST remplace cet aperçu optimiste par
      // les métadonnées complètes du serveur, filename compris.
      filename: null,
    }));
    store.sendError.value = null;
    const sent = wsRef.current?.send({
      type: 'message',
      conversationId: convId,
      content,
      ...(attachmentIds.length > 0 ? { attachmentIds } : {}),
    });
    if (sent) {
      batch(() => {
        if (retryLocalId) {
          // Relance : le message est déjà affiché (avec ses photos), on lève
          // seulement le marqueur "Non envoyé".
          markSendRetried(store, retryLocalId);
        } else {
          appendMessage(store, {
            id: localId,
            role: 'visitor',
            content,
            createdAt: Date.now(),
            ...(optimisticAttachments.length > 0 ? { attachments: optimisticAttachments } : {}),
          });
        }
        // Réconciliation par ack (cf. handleIncoming) : mémorise l'id local
        // dans l'ordre d'envoi, pour que l'ack serveur sache quel message
        // remplacer.
        trackLocalSend(store, localId);
        // Ne montrer la bulle « … » qu'en mode IA active (contrat bug) :
        // en mode 'off' (agent humain uniquement), aucune réponse IA
        // n'arrivera jamais pour la faire disparaître.
        startAiThinking(store);
      });
      savePersistedState(store);
      if (store.aiThinking.value) {
        scheduleAiThinkingTimeout();
      }
      if (isFirstVisitorMessage) {
        // En ligne, invitation douce à laisser un e-mail si personne ne
        // répond sous 120s (contrat 2.2). N'a pas d'effet si offline (le
        // formulaire bloquant est déjà affiché) ou si l'e-mail est déjà
        // capturé.
        scheduleSoftInvite();
      }
    } else if (!retryLocalId) {
      // Bug widget §2 (audit 30/09) : `send()` renvoie `false` quand le
      // socket n'est pas OPEN (course entre l'état affiché "connected" et
      // l'état réel, ou coupure survenue entre-temps). Avant ce correctif,
      // rien n'était affiché ni retenté : le message disparaissait
      // silencieusement. On le garde visible, marqué "Non envoyé", avec un
      // bouton "Réessayer" — et une relance automatique est tentée dès la
      // prochaine reconnexion (cf. retryFailedMessages ci-dessous).
      appendMessage(store, {
        id: localId,
        role: 'visitor',
        content,
        createdAt: Date.now(),
        failed: true,
        // Les photos restent attachées au message en échec : la relance
        // renvoie les mêmes `attachmentIds` (cf. composedFromMessage).
        ...(optimisticAttachments.length > 0 ? { attachments: optimisticAttachments } : {}),
      });
      savePersistedState(store);
    }
    // Si c'est une relance qui échoue à nouveau (retryLocalId défini et
    // `sent` faux), le message reste marqué `failed` sans changement — il
    // sera retenté à la prochaine reconnexion ou au prochain clic manuel.
  }

  /**
   * Relance automatique des messages visiteur restés "Non envoyé" — appelée
   * à chaque (re)connexion WS réussie. Idempotente : un message déjà envoyé
   * n'est plus dans `store.messages` avec `failed: true` (markSendRetried
   * l'a nettoyé), donc jamais relancé deux fois pour le même id.
   */
  function retryFailedMessages(): void {
    const failed = store.messages.value.filter((m) => m.role === 'visitor' && m.failed);
    for (const m of failed) {
      void deliver(m.content, composedFromMessage(m), m.id);
    }
  }

  function retrySend(localId: string): void {
    const msg = store.messages.value.find((m) => m.id === localId && m.failed);
    if (!msg) return;
    void deliver(msg.content, composedFromMessage(msg), localId);
  }

  async function send(content: string, composed: ComposedAttachment[] = []): Promise<void> {
    if (composed.length > MAX_ATTACHMENTS) return;
    if (
      isSendBlocked({
        availability: store.availability.value,
        emailCaptured: store.emailCaptured.value,
      })
    ) {
      // Hors ligne et pas encore d'e-mail : on garde le message en attente,
      // le formulaire de contact (toujours affiché dans ce cas) doit être
      // validé avant tout envoi (contrat 2.2). Les photos, elles, sont déjà
      // téléversées : seuls leurs ids attendent ici.
      store.pendingMessage.value = { content, attachments: composed };
      return;
    }
    await deliver(content, composed);
  }

  async function onContactSubmit(email: string, name: string): Promise<void> {
    try {
      const convId = await ensureConversation();
      const res = await fetch(`${ctx.backendUrl}/api/visitor/contact`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ conversationId: convId, email, ...(name ? { name } : {}) }),
      });
      if (!res.ok) return;
      batch(() => {
        store.emailCaptured.value = true;
        store.contactEmail.value = email;
        store.contactName.value = name || null;
        store.showSoftInvite.value = false;
      });
      savePersistedState(store);
      const pending = store.pendingMessage.value;
      if (pending) {
        store.pendingMessage.value = null;
        await deliver(pending.content, pending.attachments);
      }
    } catch {
      /* laisser le formulaire affiché, le visiteur peut réessayer */
    }
  }

  function onSoftInviteDismiss(): void {
    store.showSoftInvite.value = false;
  }

  function start(): void {
    if (started.value) return;
    started.value = true;
    // Premier clic : c'est seulement à partir d'ici que le localStorage
    // peut être lu (contrat 2.1).
    const persisted = loadPersistedState();
    if (persisted) {
      batch(() => {
        if (persisted.conversationId) setConversationId(store, persisted.conversationId);
        if (persisted.messages) store.messages.value = persisted.messages;
        if (persisted.emailCaptured) store.emailCaptured.value = true;
        if (persisted.contactEmail) store.contactEmail.value = persisted.contactEmail;
        if (persisted.contactName) store.contactName.value = persisted.contactName;
      });
    }
  }

  function toggle(): void {
    const wasStarted = started.value;
    if (!wasStarted) start();
    const opening = !store.isOpen.value;
    store.isOpen.value = opening;
    if (opening) {
      store.unreadCount.value = 0;
    }
    savePersistedState(store);
  }

  function retry(): void {
    triggerReconnect.value++;
  }

  return (
    <>
      <FloatingButton
        isOpen={store.isOpen.value}
        unreadCount={store.unreadCount.value}
        onClick={toggle}
        theme={theme}
      />
      {store.isOpen.value && (
        <ChatWindow
          messages={store.messages.value}
          status={store.status.value}
          escalation={store.escalation.value}
          aiThinking={store.aiThinking.value}
          availability={store.availability.value}
          availabilityMessage={store.availabilityMessage.value}
          aiMode={store.aiMode.value}
          emailCaptured={store.emailCaptured.value}
          showSoftInvite={store.showSoftInvite.value}
          hasPendingMessage={store.pendingMessage.value !== null}
          privacyUrl={ctx.privacyUrl || DEFAULT_PRIVACY_URL}
          onSend={send}
          onUpload={uploadAttachment}
          sendError={store.sendError.value}
          onDismissSendError={() => {
            store.sendError.value = null;
          }}
          onClose={toggle}
          onRetry={retry}
          onContactSubmit={onContactSubmit}
          onSoftInviteDismiss={onSoftInviteDismiss}
          onRetrySend={retrySend}
          theme={theme}
        />
      )}
    </>
  );
}
